import { isAxiosError } from 'axios';

import { useAxios } from '@/hooks/useAxios';
import { getQueryKey, useGetUserKey } from '@/hooks/useQueryKeys';
import { useQuery } from '@/utils/api';

/**
 * On-demand cost summary returned by both the per-conversation
 * (`/planner/{uuid}/cost`) and per-hunt (`/hunt/{uuid}/cost`) endpoints.
 * Both dimensions return this identical shape. `cost` is a USD float;
 * `truncated` is true when the underlying Bifrost log page cap was hit
 * (the total is then a partial lower bound).
 */
export interface CostStatus {
  cost: number;
  currency: string;
  call_count: number;
  window_start: string;
  window_end: string;
  truncated: boolean;
}

/**
 * Reads the on-demand USD cost for a single AI Assistant conversation.
 * Customer-facing (`EntitlementConversationAI`). Returns `null` when the
 * conversation UUID is not found in the caller's partition (backend 404);
 * a `200` with `cost: 0` (no spend yet / unprovisioned tenant) renders as
 * `$0.00`, NOT hidden — only `null` (404) hides the chip.
 */
export const useConversationCost = (conversationId?: string) => {
  const axios = useAxios();

  const queryKey = useGetUserKey(
    getQueryKey.getConversationCost(conversationId ?? '')
  );

  return useQuery<CostStatus | null>({
    queryKey,
    enabled: !!conversationId,
    queryFn: async () => {
      try {
        const { data } = await axios.get<CostStatus>(
          `/planner/${encodeURIComponent(conversationId ?? '')}/cost`
        );
        return data;
      } catch (err) {
        if (isAxiosError(err) && err.response?.status === 404) {
          return null;
        }
        throw err;
      }
    },
    retry: false,
    staleTime: 30_000,
    gcTime: 300_000,
    defaultErrorMessage: 'Failed to load conversation cost',
  });
};
